import {
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  inject,
  model,
  signal,
  viewChild,
} from '@angular/core';
import { AlertFlashService, UiBtnDirective } from '@callrail/looky/ui';
import {
  LoginFlow,
  MigrationService,
} from '../../services/migration/migration.service';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs';
import {
  email,
  form,
  FormField,
  minLength,
  required,
} from '@angular/forms/signals';
import { CodeInputComponent } from '../code-input/code-input.component';
import { FormsModule } from '@angular/forms';
import {
  CognitoAuthService,
  CognitoSignInResult,
} from '../../services/cognito-auth/cognito-auth.service';
import { PostLoginService } from '../../services/post-login/post-login.service';

@Component({
  selector: 'anbs-email-first-login',
  imports: [FormField, UiBtnDirective, CodeInputComponent, FormsModule],
  templateUrl: './email-first-login.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmailFirstLoginComponent {
  private alertFlash = inject(AlertFlashService);
  private migrationService = inject(MigrationService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private cognitoAuth = inject(CognitoAuthService);
  private postLogin = inject(PostLoginService);
  credentials = model({
    email: '',
    password: '',
  });
  credentialsForm = form(this.credentials, (creds) => {
    required(creds.email);
    email(creds.email);
    required(creds.password);
    minLength(creds.password, 6);
  });
  mfaFormModel = model({
    code: '',
  });
  mfaForm = form(this.mfaFormModel, (mfa) => {
    required(mfa.code, { message: 'is required' });
    minLength(mfa.code, 6, { message: 'must be 6 digits' });
  });
  otpContinue = viewChild<ElementRef>('otpContinue');
  formState = signal<'enterEmail' | 'login' | 'mfa' | 'resetPassword'>(
    'enterEmail'
  );
  showPassword = signal(false);
  submitting = signal(false);
  loginFlow = signal<LoginFlow>('cognito');

  private passwordEl = viewChild<ElementRef>('password');
  private emailEl = viewChild<ElementRef>('email');
  private codeInput = viewChild(CodeInputComponent);

  constructor() {
    effect(() => {
      if (this.mfaFormModel().code.length === 6) {
        this.otpContinue()?.nativeElement.focus();
        this.otpContinue()?.nativeElement.click();
      }
    });
  }

  continue() {
    switch (this.formState()) {
      case 'enterEmail':
        if (this.credentialsForm.email().invalid()) return;
        this.submitting.set(true);
        this.migrationService
          .checkEmail$(this.credentialsForm.email().value())
          .pipe(finalize(() => this.submitting.set(false)))
          .subscribe({
            next: (resp) => {
              this.loginFlow.set(resp.login_flow);
              this.formState.set('login');
              setTimeout(() => this.passwordEl().nativeElement.focus(), 0);
            },
            error: () => this.alertFlash.addDanger(),
          });
        break;
      case 'login':
        if (this.credentialsForm().invalid()) return;
        if (this.loginFlow() === 'stytch') {
          this.formState.set('mfa');
          return;
        }
        this.signInWithCognito();
        break;
      case 'mfa':
        if (this.mfaForm().invalid()) return;
        if (this.loginFlow() !== 'stytch') this.handleCognitoMFA();
        break;
    }
  }

  private signInWithCognito() {
    const credentials = this.credentials();
    this.submitting.set(true);
    this.cognitoAuth
      .signIn$(credentials.email, credentials.password)
      .pipe(finalize(() => this.submitting.set(false)))
      .subscribe({
        next: (result) => this.handleCognitoSignIn(result),
        error: (err: { message?: string }) =>
          this.alertFlash.addDanger(err?.message),
      });
  }

  private handleCognitoSignIn(result: CognitoSignInResult) {
    switch (result.status) {
      case 'signedIn':
        this.postLogin.redirect(this.route.snapshot.queryParamMap);
        break;
      case 'mfaRequired':
        this.formState.set('mfa');
        break;
      case 'unsupportedChallenge':
        this.router.navigateByUrl(
          `/?email=${encodeURIComponent(this.credentials().email)}`,
          { replaceUrl: true }
        );
        break;
    }
  }

  private handleCognitoMFA() {
    this.submitting.set(true);
    this.cognitoAuth
      .confirmSignIn$(this.mfaForm.code().value())
      .pipe(finalize(() => this.submitting.set(false)))
      .subscribe({
        next: (resp) => {
          this.handleCognitoSignIn(resp);
        },
        error: (err) => {
          this.alertFlash.addDanger(err?.message);
          this.mfaForm().reset({ code: '' });
          this.codeInput().otpCode()?.nativeElement.focus();
        },
      });
  }

  togglePassword() {
    this.showPassword.update((shown) => !shown);
  }

  goBack() {
    this.credentials.set({ email: '', password: '' });
    this.mfaFormModel.set({ code: '' });
    this.credentialsForm().reset();
    this.mfaForm().reset();
    this.formState.set('enterEmail');
    setTimeout(() => {
      this.emailEl().nativeElement.focus();
    }, 0);
  }

  resetPassword() {
    if (this.credentialsForm.email().invalid()) return;
    this.alertFlash.addSuccess('Please check your email.');
    this.formState.set('resetPassword');
  }
}
